/**
 * Split assistant content into prose and generative-UI blocks (tdd-0059 Phase 5).
 *
 * Generated UI arrives as a fenced block inside an otherwise normal message:
 *
 *     Here are today's numbers:
 *
 *     ```json-render
 *     {"op":"add","path":"/root","value":"main"}
 *     {"op":"add","path":"/elements/main","value":{...}}
 *     ```
 *
 *     Want me to break down any of these?
 *
 * The fence is what makes prose and components interleave in one message —
 * tdd-0012 specified this shape, and chat2's "output ONLY JSONL" prompt is
 * relaxed to match. A fence (rather than a bare JSONL run) means the parser
 * never has to guess whether a line starting with `{` is UI or something the
 * model happened to write, and an unterminated block during streaming is
 * unambiguous rather than half-parsed.
 *
 * Deliberately free of React and of `@json-render` so it can be unit-tested and
 * so the host app decides what to do with a block (chat renders it; another
 * surface might ignore it).
 */

/** A run of ordinary prose. */
export interface ProseChunk {
  type: "text";
  content: string;
}

/** A generative-UI block. `content` is the raw JSONL. */
export interface ComponentChunk {
  type: "component";
  content: string;
  /**
   * True while the closing fence has not arrived yet — i.e. the block is still
   * streaming. Renderers use this to show a placeholder instead of compiling a
   * half-written spec into a broken tree.
   */
  partial: boolean;
}

export type ContentChunk = ProseChunk | ComponentChunk;

/** The fence language tag that marks a generative-UI block. */
export const COMPONENT_FENCE = "json-render";

// Opening fence: ``` optionally followed by the tag, on its own line.
const OPEN_RE = new RegExp("^[ \\t]*```[ \\t]*" + COMPONENT_FENCE + "[ \\t]*$");
// Closing fence: bare ``` on its own line.
const CLOSE_RE = /^[ \t]*```[ \t]*$/;

/**
 * Split content into ordered prose and component chunks.
 *
 * Content with no component fence returns a single text chunk, so callers can
 * use this unconditionally without paying for a special case.
 */
export function splitComponentBlocks(content: string): ContentChunk[] {
  if (!content.includes("```")) {
    const trimmed = content.trim();
    return trimmed ? [{ type: "text", content: trimmed }] : [];
  }

  const lines = content.split("\n");
  const chunks: ContentChunk[] = [];

  let prose: string[] = [];
  let block: string[] | null = null;

  const flushProse = () => {
    const text = prose.join("\n").trim();
    if (text) chunks.push({ type: "text", content: text });
    prose = [];
  };

  for (const line of lines) {
    if (block === null) {
      if (OPEN_RE.test(line)) {
        flushProse();
        block = [];
      } else {
        prose.push(line);
      }
      continue;
    }

    if (CLOSE_RE.test(line)) {
      chunks.push({
        type: "component",
        content: block.join("\n").trim(),
        partial: false,
      });
      block = null;
      continue;
    }

    block.push(line);
  }

  // An unterminated block means the message is still streaming. Emit it as
  // partial rather than dropping it — the user should see *something* is
  // arriving — but never as prose, which would dump raw JSONL into the chat.
  if (block !== null) {
    const content = block.join("\n").trim();
    if (content) chunks.push({ type: "component", content, partial: true });
  } else {
    flushProse();
  }

  return chunks;
}

/** Whether content contains (or has begun) a generative-UI block. */
export function hasComponentBlock(content: string): boolean {
  return OPEN_RE.test(content) || content.split("\n").some((l) => OPEN_RE.test(l));
}
